import { z } from 'zod';

export const ERROR_CODES = [
  'validation_failed',
  'unauthenticated',
  'forbidden',
  'capability_disabled',
  'capability_unavailable',
  'not_found',
  'revision_conflict',
  'duplicate',
  'rate_limited',
  'provider_unavailable',
  'provider_unconfigured',
  'payload_too_large',
  'unsupported_media_type',
  'internal_error',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    correlationId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

export type ApiEnvelope<T, M = undefined> = M extends undefined ? { data: T } : { data: T; meta: M };

export const pageMetaSchema = z.object({ nextCursor: z.string().nullable() });
export type PageMeta = z.infer<typeof pageMetaSchema>;

export const limitSchema = z.coerce.number().int().min(1).max(100).default(30);

/** Opaque cursors: base64url JSON of the last sort key plus stable id tie-breaker. */
export function encodeCursor(value: Record<string, string | number | null>): string {
  const json = JSON.stringify(value);
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeCursor(cursor: string | undefined | null): Record<string, string | number | null> | null {
  if (!cursor) return null;
  try {
    const b64 = cursor.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Record<string, string | number | null>;
  } catch {
    return null;
  }
}

/** Optimistic concurrency: every mutation of an existing record sends the revision it edited. */
export const expectedRevisionSchema = z.number().int().min(0);
